/**
 * Details step — split layout: Calendar (left 60%) | Form (right 40%).
 */
import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import { Box, Flex } from '@chakra-ui/react';
import type { FormBuilderHandle, CalendarHandle } from '@kula-ai/components';
import type { StepFormApi } from '@/hooks/useStepValidation';
import { useShowToast } from '@/hooks';
import { useKFlags } from '@/hooks/use-k-flags';
import { getAtsJobCreate } from '@/store/atsJobCreate';
import { selectCurrUser } from '@/store/bootstrap/selectors';
import type { ScheduleStepProps } from '../../config/types';
import { isAdhocMode, isSelfScheduleMode } from '../../config/modeClassifiers';
import { DetailsForm } from './DetailsForm';
import { useScheduleSharedState } from '../../state/useScheduleSharedState';
import { CalendarPanel } from '../../calendar/CalendarPanel';
import {
  CALENDAR_PREFETCH_WEEKS,
  CALENDAR_LOOKAHEAD_WEEKS,
} from '../../calendar/constants';
import { NoSlotsAvailablePopover } from '../../calendar/NoSlotsAvailablePopover';
import { useFormCalendarSync } from '../../calendar/useFormCalendarSync';
import {
  getMinimumNoticeHours,
  type MinimumNoticeSource,
} from '../../availability';
import { useAccountMinimumNotice } from '../../api/useAccountMinimumNotice';
import {
  SlotsPreviewModal,
  type FindTimeValue,
  type SelfScheduleHandle,
  type SlotsPreviewData,
} from '../../calendar/selfSchedule';
import { useDetailsBuffer } from './hooks/useDetailsBuffer';
import { useFindTimeController } from './hooks/useFindTimeController';
import { buildInitialSelfScheduleSlots } from './helpers/buildInitialSelfScheduleSlots';
import { resolveBufferFlagOn } from './helpers/resolveBufferFlagOn';
import { useDetailsStepSave } from './hooks/useDetailsStepSave';
import { useScheduleHostSelection } from './hooks/useScheduleHostSelection';
import type { CurrentUserZoomStatus } from '../../initialValues/useWizardInitialValues';
import type { InterviewerWithoutTimezone } from './timezoneModal';

interface DetailsStepProps extends ScheduleStepProps {
  interviewPlan?: Record<string, unknown>;
  /** Adhoc-only fallback for the current user's Zoom status. Non-adhoc
   *  flows read zoom status off `interviewPlan` directly; adhoc keeps
   *  `interviewPlan` undefined (so downstream behavioral branches stay
   *  off) and surfaces just this slice for the Host chip. */
  currentUserZoomStatus?: CurrentUserZoomStatus;
  /** Adhoc-only fallback for the plan-level zoom host strategy. Drives
   *  `pickAutoHostId` priority — non-adhoc reads `interviewPlan?.zoom_host_type`
   *  directly; adhoc surfaces just this slice from the plan response. */
  planZoomHostType?: 'organizer' | 'interviewer' | string | null;
  /** Adhoc-only fallback for the note-taker mandate. Non-adhoc flows read
   *  `note_taker_mandatory` off `interviewPlan`; adhoc keeps `interviewPlan`
   *  undefined and surfaces just this fresh slice off the plan response so
   *  the mandate guards + toggle lock stop depending on the never-populated
   *  `atsJobCreate.currentJob` slice (ENG-3339). */
  planNoteTakerMandatory?: boolean;
  /** Account-level meeting-rooms gate (all modes, incl. adhoc). Drives the
   *  "Book a meeting room" checkbox + room-availability fetch. */
  meetingRoomsEnabled?: boolean;
  candidateName?: string;
  candidateAvatar?: string;
}

export const DetailsStep = memo(
  forwardRef<StepFormApi, DetailsStepProps>(
    (
      {
        payloadRef,
        mode,
        apiReference,
        prefillVersion = 0,
        interviewPlan,
        currentUserZoomStatus,
        planZoomHostType: planZoomHostTypeProp,
        planNoteTakerMandatory,
        meetingRoomsEnabled,
        candidateName,
        candidateAvatar,
        candidatePhone,
        candidatePhoneValue,
        atsJobId,
        atsInterviewId,
        originSelfSchedule,
        atsJobStageActionId,
        additionalSlotsContext,
        interviewPlatform,
        candidateAvailabilitySlots,
      },
      ref
    ) => {
      const formRef = useRef<FormBuilderHandle>(null);
      const calendarRef = useRef<CalendarHandle>(null);
      const selfScheduleRef = useRef<SelfScheduleHandle>(null);
      const tzModalRef = useRef<{
        open: (_interviewers: InterviewerWithoutTimezone[]) => void;
      } | null>(null);
      const toast = useShowToast();
      // Default the calendar timezone to the logged-in user's profile
      // timezone (legacy KInterviewScheduler parity — it used
      // `currentUser.time_zone || moment.tz.guess()`). Seeded once into the
      // store; edit mode overrides this below with the saved interview's
      // timezone. Falls back to the shared-state browser guess when the
      // profile has no timezone.
      const currUser = useSelector(selectCurrUser, shallowEqual) as
        | { time_zone?: string }
        | undefined;
      const currUserTimezone = currUser?.time_zone;
      const { state: sharedState, update: updateSharedState } =
        useScheduleSharedState(
          currUserTimezone ? { timezone: currUserTimezone } : undefined
        );

      const isSelfSchedule = isSelfScheduleMode(mode);
      const isAdhoc = isAdhocMode(mode);

      // Note-taker mandate. Prefer the FRESH per-stage value from the
      // plan/edit response (`interviewPlan.note_taker_mandatory`,
      // GET /ats_interviews/new|:id — scheduler-accessible, correctly
      // scoped) over the shared `atsJobCreate.currentJob` slice, which the
      // wizard never refetches and can leave at a stale `true` after the
      // account mandate master is switched off. Adhoc has no `interviewPlan`
      // but the plan IS fetched for it, so its fresh mandate arrives via
      // `planNoteTakerMandatory` — used here instead of the `currentJob`
      // slice, which the candidate/adhoc entry never populates (ENG-3339).
      // Threaded into the save-time defensive write in
      // `useDetailsStepSave.syncToPayload` and the race-recovery effect below.
      const { currentJob = {} } = useSelector(getAtsJobCreate, shallowEqual);
      const freshNoteTakerMandatory =
        interviewPlan?.note_taker_mandatory ?? planNoteTakerMandatory;
      const isNoteTakerMandatory = Boolean(
        freshNoteTakerMandatory !== undefined
          ? freshNoteTakerMandatory
          : (currentJob as Record<string, unknown>)?.data?.note_taker?.mandatory
      );

      // Race recovery: the seed override in `useWizardInitialValues`
      // sets `enable_note_taker = true` when mandate is on, but only at
      // the moment the form first hydrates. If
      // `currentJob.data.note_taker.mandatory` lands from Redux AFTER
      // hydrate (common on cold loads — the job query and the activity
      // wizard race), the override is missed: the form mounts with the
      // API-supplied `false` and the toggle later renders disabled +
      // required + locked to false. The recruiter has no recovery and
      // a virtual-location save trips `err_note_taker_mandatory`.
      //
      // Effect re-fires on (a) mandate transition true and (b) live
      // location change — covers both the Redux-late-load race and a
      // user switching back to a virtual location after FormBuilder
      // cleared the value on a non-virtual switch (preserveValue is
      // false on the note-taker condition by design).
      //
      // Defensive payload guard in `syncToPayload` separately forces
      // the field at save time; this effect keeps the visible form
      // state aligned so the disabled toggle doesn't read as "off"
      // while the BE actually receives "on".
      useEffect(() => {
        if (!isNoteTakerMandatory) return;
        const form = formRef.current;
        if (!form) return;
        const values = form.getValues() as Record<string, unknown> | undefined;
        const liveLocation = values?.location;
        if (
          liveLocation !== 'zoom' &&
          liveLocation !== 'google_meet' &&
          liveLocation !== 'teams'
        ) {
          return;
        }
        if (values?.enable_note_taker === true) return;
        form.setValue('enable_note_taker', true);
        payloadRef.current.details.enable_note_taker = true;
      }, [isNoteTakerMandatory, sharedState.location, payloadRef]);

      const { feInterviewBuffer, feManualInterviewBuffer } = useKFlags() as {
        feInterviewBuffer?: boolean;
        feManualInterviewBuffer?: boolean;
      };
      // FE rollout gate for the buffer code in this mode: self-schedule
      // behind `feInterviewBuffer`, manual/adhoc behind
      // `feManualInterviewBuffer`. A booked interview that originated from
      // a self-schedule is edited in `mode="manual"` but still follows the
      // self-schedule flag (`originSelfSchedule`). Final visibility ANDs in
      // the account-level setting below.
      const bufferFlagOn = resolveBufferFlagOn({
        mode,
        apiReference,
        feInterviewBuffer: Boolean(feInterviewBuffer),
        feManualInterviewBuffer: Boolean(feManualInterviewBuffer),
        originSelfSchedule,
      });
      // Account-effective buffer flag read straight off the plan on
      // CREATE (`/ats_interviews/new` pre-fills it from `AccountSetting`,
      // BE `effective_buffer_enabled?` — already LD-gated). EDIT can't use
      // this (the plan is the saved interview, not the account default),
      // so it falls back to the async account fetch below.
      const planBufferEnabled =
        apiReference !== 'edit' &&
        (interviewPlan?.interviewer_buffer_enabled as boolean | undefined) ===
          true;

      // Resolve minimum-notice hours for the calendar + Find Time. Normal
      // self-schedule reads it off the plan response; adhoc / bulk have
      // no plan and need the account-level setting fetched directly.
      // Fetch only fires when the plan path produced nothing — normal
      // self-schedule never triggers this query.
      const planMinimumNoticeHours = getMinimumNoticeHours(
        interviewPlan as MinimumNoticeSource | undefined
      );
      const { data: accountMinimumNotice } = useAccountMinimumNotice({
        enabled: isSelfSchedule && planMinimumNoticeHours <= 0,
      });
      const minimumNoticeHours =
        planMinimumNoticeHours > 0
          ? planMinimumNoticeHours
          : getMinimumNoticeHours(accountMinimumNotice);

      // Buffer concern (account setting fetch + one-shot adhoc seed +
      // change handler + the account-level master-switch flag).
      const { handleBufferChange, accountBufferEnabled } = useDetailsBuffer({
        bufferFlagOn,
        apiReference,
        interviewPlan,
        updateSharedState,
      });
      // Account-level buffer is the master switch: hide the per-interview
      // control entirely when the account has buffer off, in every mode
      // (incl. edit). When on, CREATE defaults the toggle on via the
      // plan/account hydration in `fromPlan` / `useDetailsBuffer`.
      const isBufferEnabledByFlag =
        bufferFlagOn && (planBufferEnabled || accountBufferEnabled);

      // Find Time + slots-preview + availability UI — extracted to
      // `useFindTimeController` (pure file split). `scheduleContextRef`
      // stays below (save-time payload input, not Find Time).
      const {
        findTimeValue,
        setFindTimeValue,
        ignoreNextSlotEmitRef,
        handleFindTimeApply,
        handleFindTimeClear,
        isPreviewOpen,
        handlePreviewOpen,
        handlePreviewClose,
        selfScheduleSlotsForPreview,
        availableDays,
        handleAvailableDaysChange,
        noAvailabilityKey,
        handleFindTimeNoAvailability,
        clearNoAvailability,
      } = useFindTimeController({ selfScheduleRef, sharedState });

      // Snapshot of the inputs the payload mapper needs to classify
      // each self-schedule slot's `is_custom` / `override_type` —
      // pushed up by `CalendarPanel.onScheduleContextChange` whenever
      // either side changes (gated on the panel side via content key
      // so it doesn't re-render us). Lives in a ref because we only
      // read it at save time inside `syncToPayload`; storing in state
      // would re-render the whole tree on every busy refetch for no
      // visual benefit.
      //
      // `isReady` starts `false` and flips `true` once the busy +
      // working-hours queries have settled. `validate()` blocks the
      // step transition while it's `false` so the user can't ship a
      // payload that classifies grey/busy slots as `'none'` purely
      // because the queries hadn't resolved yet (G1, send-additional-slots).
      const scheduleContextRef = useRef<{
        workingHoursByInterviewer: Record<
          string,
          Array<{ start: string; end: string }>
        >;
        busyEvents: ReadonlyArray<{
          id: string;
          start: string;
          end: string;
          interviewerId: string | number;
        }>;
        coveredRanges: ReadonlyArray<{ start: string; end: string }>;
        isReady: boolean;
      }>({
        workingHoursByInterviewer: {},
        busyEvents: [],
        coveredRanges: [],
        isReady: false,
      });
      const handleScheduleContextChange = useCallback(
        (ctx: {
          workingHoursByInterviewer: Record<
            string,
            Array<{ start: string; end: string }>
          >;
          busyEvents: ReadonlyArray<{
            id: string;
            start: string;
            end: string;
            interviewerId: string | number;
          }>;
          coveredRanges: ReadonlyArray<{ start: string; end: string }>;
          isReady: boolean;
        }) => {
          scheduleContextRef.current = ctx;
        },
        []
      );

      // Edit-mode seed for the self-schedule calendar's reducer.
      // Read synchronously from `payloadRef` so `useReducer`'s lazy
      // init picks them up on first render — routing through shared
      // state alone would arrive a tick late and the reducer would
      // already be seeded empty.
      // Pure computation extracted to `buildInitialSelfScheduleSlots`
      // (file split + unit test). The memo wrapper + deps stay here:
      // `payloadRef.current` is read synchronously and `prefillVersion`
      // forces recompute on re-hydration (hence the eslint-disable).
      const initialSelfScheduleSlots = useMemo(
        () =>
          buildInitialSelfScheduleSlots({
            apiReference,
            isSelfSchedule,
            details: payloadRef.current.details as Record<string, unknown>,
            additionalSlotsContext,
          }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
          apiReference,
          isSelfSchedule,
          payloadRef,
          prefillVersion,
          additionalSlotsContext,
        ]
      );

      // Seed `findTimeValue` from the hydrated preset once on mount.
      const hydratedFindTimeRef = useRef(false);
      useEffect(() => {
        if (hydratedFindTimeRef.current) return;
        if (apiReference !== 'edit' || !isSelfSchedule) return;
        const ftv = (payloadRef.current.details as Record<string, unknown>)
          .findTimeValue as FindTimeValue | undefined;
        if (ftv) {
          hydratedFindTimeRef.current = true;
          setFindTimeValue(ftv);
        }
        // `setFindTimeValue` is a stable setter (now from
        // useFindTimeController) — listed for exhaustive-deps; identity
        // never changes so behaviour is unaffected.
      }, [
        apiReference,
        isSelfSchedule,
        payloadRef,
        prefillVersion,
        setFindTimeValue,
      ]);

      const { handleFieldChange } = useFormCalendarSync({
        formRef,
        sharedState,
        updateSharedState,
        calendarRef,
      });

      // Seed duration from the interview plan so edit mode + presets
      // reach the calendar overlay before the user touches the form.
      useEffect(() => {
        const planDuration =
          typeof interviewPlan?.duration === 'number' &&
          interviewPlan.duration > 0
            ? interviewPlan.duration
            : 30;
        if (planDuration !== sharedState.duration) {
          updateSharedState({ duration: planDuration });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [interviewPlan?.duration]);

      // Edit: seed sharedState from the hydrated payloadRef. Create
      // (with an interview plan): seed the same fields from the plan
      // so the calendar fires its busy-events fetch for the plan's
      // default interviewers — otherwise sharedState stays empty
      // until the user manually changes a field, and the calendar
      // shows a blank week even though the form already has the
      // interviewers prefilled.
      useEffect(() => {
        const updates: Partial<typeof sharedState> = {};
        if (apiReference === 'edit') {
          const details = payloadRef.current.details as Record<string, unknown>;
          const bs = details.bookedSlot as
            | { start?: string; end?: string; duration?: number }
            | undefined;
          if (bs?.start && bs?.end) {
            updates.bookedSlot = {
              start: bs.start,
              end: bs.end,
              duration: bs.duration ?? sharedState.duration,
            };
          }
          // Mirror the hydrated duration into top-level `sharedState.duration`
          // — the calendar's click-to-move handler reads it as the slot
          // length when the user clicks a 15-min cell to move the booked
          // event (`useCalendarEventHandlers.handleSelect` builds
          // `end = start + duration`). The plan-duration seed effect above
          // can't pick this up because GET `/ats_interviews/:id` does NOT
          // return a top-level `duration` field — it's computed from
          // `(end_time - start_time)` inside `hydrateDetailsFromApi` and
          // landed on `payloadRef.details.duration`. Without this mirror,
          // sharedState.duration stays at the default 30 and a 2-min
          // interview silently becomes 30 mins on first click-to-move.
          const detailsDuration = details.duration;
          if (
            typeof detailsDuration === 'number' &&
            detailsDuration > 0 &&
            detailsDuration !== sharedState.duration
          ) {
            updates.duration = detailsDuration;
          }
          // Same pattern for `interviewKind` — sharedState defaults to
          // `'one_on_one'` and the calendar's availability algorithm uses
          // it to pick union (one_on_one) vs intersection (panel) of
          // interviewers' free time. Without this seed, editing a panel
          // interview shows union availability until the user touches
          // the interview type field. `useFormCalendarSync` already
          // syncs subsequent user changes via the `interview_type` case.
          const detailsKind = details.interview_type;
          if (
            (detailsKind === 'panel' || detailsKind === 'one_on_one') &&
            detailsKind !== sharedState.interviewKind
          ) {
            updates.interviewKind = detailsKind;
          }
          // Self-schedule edit: seed shared state with the hydrated
          // time slots so the calendar's reducer sees them on first
          // paint. Without this the pre-selected slots silently fall
          // away — payloadRef has them, but the form-side sync only
          // picked up `bookedSlot` before.
          const slots = details.selfScheduleSlots as
            | Array<{
                start: string;
                end: string;
                duration?: number;
                is_custom?: boolean;
                override_type?:
                  | 'none'
                  | 'calendar'
                  | 'working_hours'
                  | 'calendar_working_hours'
                  | 'buffer'
                  | 'buffer_calendar'
                  | 'buffer_working_hours'
                  | 'wh_cal_buffer';
              }>
            | undefined;
          if (
            isSelfSchedule &&
            Array.isArray(slots) &&
            slots.length > 0 &&
            (sharedState.selfScheduleSlots?.length ?? 0) === 0
          ) {
            // Mark hydrated slots as `_fromServer: true` so the payload
            // mapper preserves their override classification verbatim
            // until the user moves them. Stripping this would mean
            // every save recomputes — fine in theory, but legacy
            // preserves server values to avoid drift if the backend
            // classification logic differs from ours (it does — backend
            // has account-level working-hours overrides we don't model
            // here).
            updates.selfScheduleSlots = slots.map((s) => ({
              start: s.start,
              end: s.end,
              duration: s.duration ?? sharedState.duration,
              ...(s.is_custom !== undefined ? { is_custom: s.is_custom } : {}),
              ...(s.override_type ? { override_type: s.override_type } : {}),
              _fromServer: true,
            }));
          }
          const bb = details.bufferBeforeMinutes;
          const ba = details.bufferAfterMinutes;
          const be = details.bufferEnabled;
          if (typeof bb === 'number') updates.bufferBeforeMinutes = bb;
          if (typeof ba === 'number') updates.bufferAfterMinutes = ba;
          if (typeof be === 'boolean') updates.bufferEnabled = be;
          const ids = details.participant_ids;
          if (Array.isArray(ids)) {
            updates.interviewerIds = ids.map(Number).filter(Number.isFinite);
          } else if (typeof ids === 'string' && ids) {
            const n = Number(ids);
            if (Number.isFinite(n)) updates.interviewerIds = [n];
          }
          const rooms = details.meeting_room_ids;
          if (Array.isArray(rooms)) {
            updates.meetingRoomEmails = rooms.map(String).filter(Boolean);
          } else if (typeof rooms === 'string' && rooms) {
            updates.meetingRoomEmails = [rooms];
          }
          // Edit: "Book a meeting room" starts checked only when the
          // interview already has a room. Seeds the calendar-overlay gate.
          updates.bookMeetingRoom =
            (updates.meetingRoomEmails ?? []).length > 0;
          const name = details.interviewName;
          if (typeof name === 'string' && name) {
            updates.interviewName = name;
            updates.originalInterviewName = name;
          }
        } else if (interviewPlan) {
          // Create with plan: read from payloadRef.details (already mapped
          // to form field names by hydratePlanDefaults) — same field names
          // as the edit branch above so guards and reads are consistent.
          const planDetails = payloadRef.current.details as Record<
            string,
            unknown
          >;
          const rawIds = planDetails.participant_ids;
          const allIds = (
            Array.isArray(rawIds) ? rawIds : rawIds ? [rawIds] : []
          )
            .map((id) => Number(id))
            .filter((n) => Number.isFinite(n) && n > 0);

          // For a one-on-one (manual mode), the plan's participants list
          // is a POOL — only ONE attends. Limit the calendar feed to the
          // first so it doesn't show busy blocks for all pool members.
          // Panel + self-schedule send everyone (all attend).
          const planKind = planDetails.interview_type as string | undefined;
          const useAllIds = planKind === 'panel' || isSelfSchedule;
          const idsForCalendar = useAllIds ? allIds : allIds.slice(0, 1);

          if (
            idsForCalendar.length > 0 &&
            sharedState.interviewerIds.length === 0
          ) {
            updates.interviewerIds = idsForCalendar;
          }

          const rawPlanRooms = planDetails.meeting_room_ids;
          const planRooms: string[] = Array.isArray(rawPlanRooms)
            ? (rawPlanRooms as string[]).filter(Boolean)
            : typeof rawPlanRooms === 'string' && rawPlanRooms
              ? [rawPlanRooms]
              : [];
          if (
            planRooms.length > 0 &&
            sharedState.meetingRoomEmails.length === 0
          ) {
            updates.meetingRoomEmails = planRooms;
          }
          // Create-from-plan: box starts checked when the plan ships rooms.
          updates.bookMeetingRoom = planRooms.length > 0;

          const planName = planDetails.interviewName as string | undefined;
          if (
            typeof planName === 'string' &&
            planName &&
            !sharedState.interviewName
          ) {
            updates.interviewName = planName;
          }

          if (
            (planKind === 'panel' || planKind === 'one_on_one') &&
            sharedState.interviewKind !== planKind
          ) {
            updates.interviewKind = planKind;
          }

          // Buffer pre-fill — `hydratePlanDefaults` wrote these onto
          // payloadRef when `feInterviewBuffer` was on at hydration time.
          // Without this propagation, the BufferField would render
          // shared-state defaults (15/15/true) and on save `syncToPayload`
          // would overwrite the plan-hydrated values with those defaults.
          const planBb = planDetails.bufferBeforeMinutes;
          const planBa = planDetails.bufferAfterMinutes;
          const planBe = planDetails.bufferEnabled;
          if (typeof planBb === 'number') updates.bufferBeforeMinutes = planBb;
          if (typeof planBa === 'number') updates.bufferAfterMinutes = planBa;
          if (typeof planBe === 'boolean') updates.bufferEnabled = planBe;
        }
        if (Object.keys(updates).length > 0) {
          updateSharedState(updates);
        }
        // Only seed on (re)mount keyed by prefillVersion (edit) or
        // when the plan becomes available (create). `updateSharedState`
        // is stable; depending on `sharedState` would loop.
        // `bufferFlagOn` (the synchronous FE flag) is in deps so a
        // mid-session flag flip re-runs the buffer-copy block. NOT
        // `isBufferEnabledByFlag` — that ANDs in the async account fetch,
        // which would flip post-mount and re-run the edit branch,
        // clobbering in-progress edits.
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [apiReference, prefillVersion, interviewPlan, bufferFlagOn]);

      const handleVisibleWeekStartChange = useCallback(
        (iso: string) => {
          updateSharedState({ calendarWeekStart: iso });
        },
        [updateSharedState]
      );

      const handleCalendarAvailabilityChange = useCallback(
        (disconnectedIds: number[]) => {
          updateSharedState({
            calendarDisconnectedInterviewerIds: disconnectedIds,
          });
        },
        [updateSharedState]
      );

      const handleSlotSelect = useCallback(
        (slot: { start: string; end: string }) => {
          // Manual-only path (self-schedule's slot collection flows
          // through `handleSlotsChange`). The dragged-slot bounds are
          // authoritative — derive duration from them so a drag-to-
          // resize on the calendar reflects on the form's Duration
          // field. Click selection keeps the existing duration
          // because `useCalendarEventHandlers.handleSelect` already
          // builds `end = start + duration` for clicks; only drags
          // produce a different span.
          const startMs = new Date(slot.start).getTime();
          const endMs = new Date(slot.end).getTime();
          const slotDuration = Math.max(
            1,
            Math.round((endMs - startMs) / 60000)
          );
          updateSharedState({
            bookedSlot: {
              start: slot.start,
              end: slot.end,
              duration: slotDuration,
            },
            duration: slotDuration,
          });
        },
        [updateSharedState]
      );

      // Host (Zoom) selection — current-user zoom status, host option
      // lists, SCH-37 lock + enforcer, CREATE-only auto-pick
      // (CORE-4039 edit-preserve), and Zoom-location auto-fallback —
      // extracted to `useScheduleHostSelection` (pure file split;
      // memo/effect bodies + dependency arrays unchanged).
      const {
        currentUserHostInfo,
        loggedInUserId,
        selectedHostParticipantOptions,
        editSavedHost,
      } = useScheduleHostSelection({
        interviewPlan,
        currentUserZoomStatus,
        payloadRef,
        sharedState,
        apiReference,
        planZoomHostType: planZoomHostTypeProp,
        isSelfSchedule,
        formRef,
      });

      // Stable callback so DetailsForm's effect doesn't re-fire every
      // render and trigger the shared-store to re-notify listeners in
      // a loop.
      const handleMeetingRoomNamesChange = useCallback(
        (namesByEmail: Record<string, string>) => {
          updateSharedState({ meetingRoomNamesByEmail: namesByEmail });
        },
        [updateSharedState]
      );

      const handleSlotsChange = useCallback(
        (
          slots: Array<{
            start: string;
            end: string;
            is_custom?: boolean;
            override_type?:
              | 'none'
              | 'calendar'
              | 'working_hours'
              | 'calendar_working_hours'
              | 'buffer'
              | 'buffer_calendar'
              | 'buffer_working_hours'
              | 'wh_cal_buffer';
            _fromServer?: boolean;
          }>
        ) => {
          updateSharedState({
            // Preserve override flags + `_fromServer` gate from the
            // calendar emit. `mapDetails` reads these at save time and
            // either passes them through (server-sourced + unmoved) or
            // recomputes via `computeSlotOverride`. Stripping them here
            // would silently send `is_custom: false` for legitimate
            // override picks, making them unbookable by candidates
            // (backend's working-hours / busy-event filters reject the
            // booking attempt). Same parity gap that motivated this
            // whole pass.
            selfScheduleSlots: slots.map((s) => ({
              start: s.start,
              end: s.end,
              duration: sharedState.duration,
              ...(s.is_custom !== undefined ? { is_custom: s.is_custom } : {}),
              ...(s.override_type ? { override_type: s.override_type } : {}),
              ...(s._fromServer ? { _fromServer: true } : {}),
            })),
          });
          // Dirty-track the Find Time selection. Any emit that isn't
          // the one triggered by our own `applyFindTime`/`clearAll`
          // call (those set `ignoreNextSlotEmitRef = true`) is a
          // manual edit and should null out the active preset.
          if (ignoreNextSlotEmitRef.current) {
            ignoreNextSlotEmitRef.current = false;
          } else if (findTimeValue !== null) {
            setFindTimeValue(null);
          }
          // Auto-dismiss the no-availability banner once any slot
          // exists. Covers all creation routes (manual drag, day
          // checkbox, late auto-fill once availability arrives) since
          // every one of them lands here. Cheap no-op when the banner
          // isn't active — clear-to-null bails fast inside React.
          if (slots.length > 0) {
            clearNoAvailability();
          }
        },
        // `ignoreNextSlotEmitRef` (ref) + `setFindTimeValue` (setter)
        // + `clearNoAvailability` are stable identities from
        // useFindTimeController — listed for exhaustive-deps; they
        // never change so the callback identity behaves exactly as
        // before the extraction.
        [
          findTimeValue,
          sharedState.duration,
          updateSharedState,
          ignoreNextSlotEmitRef,
          setFindTimeValue,
          clearNoAvailability,
        ]
      );

      // Save contract (`validate` + `syncToPayload` via
      // useImperativeHandle) — extracted to `useDetailsStepSave`
      // (pure file split; factory body + dependency array unchanged,
      // called at the same hook-order position).
      useDetailsStepSave({
        ref,
        formRef,
        payloadRef,
        tzModalRef,
        scheduleContextRef,
        sharedState,
        isSelfSchedule,
        isAdhoc,
        isBufferEnabledByFlag,
        minimumNoticeHours,
        interviewPlan,
        findTimeValue,
        toast,
        isNoteTakerMandatory,
      });

      return (
        <Flex
          key={String(prefillVersion)}
          w="100%"
          h="100%"
          minH={0}
          flex="1"
          gap="0"
        >
          {/* Left panel — Calendar */}
          <Box
            position="relative"
            flex="3"
            minW={0}
            h="100%"
            borderRight="1px solid"
            borderColor="border-color"
            overflow="hidden"
          >
            <CalendarPanel
              ref={calendarRef}
              mode={mode}
              interviewKind={sharedState.interviewKind}
              prefetchWeeks={CALENDAR_PREFETCH_WEEKS}
              lookaheadPrefetchWeeks={CALENDAR_LOOKAHEAD_WEEKS}
              duration={sharedState.duration}
              timezone={sharedState.timezone}
              interviewerIds={sharedState.interviewerIds.map(String)}
              meetingRoomIds={sharedState.meetingRoomIds.map(String)}
              // Filter on the "Book a meeting room" toggle — when off,
              // pass no rooms so their busy events leave the grid. The
              // emails themselves persist in shared state so re-ticking
              // restores the overlay without a re-fetch round-trip.
              meetingRoomEmails={
                sharedState.bookMeetingRoom ? sharedState.meetingRoomEmails : []
              }
              meetingRoomNamesByEmail={sharedState.meetingRoomNamesByEmail}
              interviewName={sharedState.interviewName}
              originalInterviewName={sharedState.originalInterviewName}
              candidateName={candidateName}
              bookedSlot={sharedState.bookedSlot}
              onSlotSelect={handleSlotSelect}
              onTimezoneChange={(tz) => updateSharedState({ timezone: tz })}
              onSlotsChange={handleSlotsChange}
              onVisibleWeekStartChange={handleVisibleWeekStartChange}
              onCalendarAvailabilityChange={handleCalendarAvailabilityChange}
              onAvailableDaysChange={handleAvailableDaysChange}
              onScheduleContextChange={handleScheduleContextChange}
              isEdit={apiReference === 'edit'}
              atsJobId={atsJobId}
              atsInterviewId={atsInterviewId}
              minimumNoticeHours={minimumNoticeHours}
              selfScheduleRef={selfScheduleRef}
              initialSelfScheduleSlots={initialSelfScheduleSlots}
              bufferBeforeMinutes={
                isBufferEnabledByFlag && sharedState.bufferEnabled
                  ? sharedState.bufferBeforeMinutes
                  : 0
              }
              bufferAfterMinutes={
                isBufferEnabledByFlag && sharedState.bufferEnabled
                  ? sharedState.bufferAfterMinutes
                  : 0
              }
              candidateAvailabilitySlots={candidateAvailabilitySlots}
            />
            {/* Find-Time "selected interviewers not available" popover.
                 Mounted here so the absolute-positioned popover overlays
                 the calendar grid (same visual home as the existing
                 "no slots this week" variant). Each fire sets a fresh
                 key, remounting the dismissable popover. */}
            {noAvailabilityKey && (
              <NoSlotsAvailablePopover
                key={noAvailabilityKey}
                visible
                reason="find-time"
              />
            )}
          </Box>

          {/* Right panel — Form */}
          <Box
            w="400px"
            maxW="400px"
            minW="400px"
            flexShrink={0}
            h="100%"
            overflowY="auto"
            px="5"
            py="4"
          >
            <DetailsForm
              ref={formRef}
              mode={mode}
              apiReference={apiReference}
              payloadRef={payloadRef}
              interviewPlan={interviewPlan}
              noteTakerMandatory={planNoteTakerMandatory}
              meetingRoomsEnabled={meetingRoomsEnabled}
              interviewPlatform={interviewPlatform}
              calendarBookedStartIso={sharedState.bookedSlot?.start ?? null}
              calendarBookedEndIso={sharedState.bookedSlot?.end ?? null}
              visibleWeekStartIso={sharedState.calendarWeekStart || null}
              disconnectedInterviewerIds={
                sharedState.calendarDisconnectedInterviewerIds
              }
              scheduleTimezone={sharedState.timezone}
              candidateName={candidateName}
              candidateAvatar={candidateAvatar}
              candidatePhone={candidatePhone}
              candidatePhoneValue={candidatePhoneValue}
              atsJobStageActionId={atsJobStageActionId}
              // Stops the AI-recommended-interviewer query as soon as
              // the user picks anyone — `useFormCalendarSync` mirrors
              // `participant_ids` into `sharedState.interviewerIds` on
              // every form change, so this flips before the next
              // render of the schema. Without it, dragging the booked
              // slot kept refetching the stale recommendation.
              hasInterviewerSelected={sharedState.interviewerIds.length > 0}
              hostParticipantOptions={selectedHostParticipantOptions}
              currentUserHostInfo={currentUserHostInfo}
              loggedInUserId={loggedInUserId}
              editSavedHost={editSavedHost}
              onFieldChange={handleFieldChange}
              onMeetingRoomNamesChange={handleMeetingRoomNamesChange}
              // Find Time — only passed in self-schedule modes. The
              // schema renders a custom row below Duration that
              // mounts `<FindTimeField>` and hides itself until at
              // least one interviewer is selected (legacy parity).
              findTimeProps={
                isSelfSchedule
                  ? {
                      value: findTimeValue,
                      slotsCount: sharedState.selfScheduleSlots?.length ?? 0,
                      timezone: sharedState.timezone ?? '',
                      minimumNoticeHours,
                      availableDays,
                      onApply: handleFindTimeApply,
                      onClear: handleFindTimeClear,
                      onPreview: handlePreviewOpen,
                      onNoAvailability: handleFindTimeNoAvailability,
                    }
                  : undefined
              }
              bufferProps={
                isBufferEnabledByFlag
                  ? {
                      value: {
                        before: sharedState.bufferBeforeMinutes,
                        after: sharedState.bufferAfterMinutes,
                        enabled: sharedState.bufferEnabled,
                      },
                      onChange: handleBufferChange,
                    }
                  : undefined
              }
              tzModalRef={tzModalRef}
            />
          </Box>
          {isSelfSchedule ? (
            <SlotsPreviewModal
              isOpen={isPreviewOpen}
              onClose={handlePreviewClose}
              data={
                {
                  src: 'kula',
                  styleProps: {},
                  name: sharedState.interviewName,
                  duration: sharedState.duration,
                  timeZone: sharedState.timezone,
                  location_type: (formRef.current?.getValues?.()?.location ??
                    'google_meet') as string,
                  contact_name: candidateName,
                  slots: selfScheduleSlotsForPreview,
                } satisfies SlotsPreviewData
              }
            />
          ) : null}
        </Flex>
      );
    }
  )
);

DetailsStep.displayName = 'DetailsStep';
